import { AutoComplete, Input } from 'antd';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SEARCH_INPUT_PROPS } from '../../../../../shared/policies/search-input-props';
import { placeSearchOptions, placeSearchPick } from '../../../public/place-search-options';
import { usePlaceSearch } from '../../../public/use-place-search';

const FILTER_DEBOUNCE_MS = 300;
/** Enough to pick from on focus, before anything is typed. */
const SUGGESTION_LIMIT = 8;

interface ZonesMapSearchProps {
  /** The map's name filter (`q` in the URL). */
  filter: string | undefined;
  onFilter: (q: string | undefined) => void;
  onPickZone: (zoneId: string) => void;
}

/**
 * The map's search box: typing filters the map to the zones whose name or code matches (after a pause, as the
 * table's search does), and the zones that match are suggested underneath, so one can be opened straight away.
 * Focusing the empty box already lists zones.
 */
export function ZonesMapSearch({ filter, onFilter, onPickZone }: ZonesMapSearchProps) {
  const { text, setText, state } = usePlaceSearch(null, {
    initialText: filter ?? '',
    includePlaces: false,
    suggestWhenEmpty: true,
    zoneLimit: SUGGESTION_LIMIT,
  });
  const [shownFilter, setShownFilter] = useState(filter);
  const term = text.trim() || undefined;

  // Follow the filter when it changes underneath the box (Back, a shared link): adjusting state during render, not in an effect.
  if (filter !== shownFilter) {
    setShownFilter(filter);
    setText(filter ?? '');
  }

  useEffect(() => {
    if (term === filter) return;
    const timer = setTimeout(() => onFilter(term), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term, filter, onFilter]);

  // Picking a suggestion also reports its option value as a change; that is not something typed to filter by.
  function change(value: string) {
    if (!placeSearchPick(value, state)) setText(value);
  }

  function select(value: string) {
    const pick = placeSearchPick(value, state);
    if (pick?.type === 'zone') onPickZone(pick.zoneId);
  }

  return (
    <AutoComplete className="w-full" value={text} options={placeSearchOptions(state)} onChange={change} onSelect={select} popupMatchSelectWidth={false}>
      <Input {...SEARCH_INPUT_PROPS} aria-label="Search zones" prefix={<Search className="size-4 text-muted" />} placeholder="Search name or code" allowClear />
    </AutoComplete>
  );
}
